import {IBistasDB, IDidaktische_kommentare_aufgabenDB, IDidaktische_kommentare_itemsDB, IDomaenenDB, IErgebnisseDB, IGruppenDB, IJsontresorDB, ILoesungshaeufigkeitenDB, ISchueler_gruppenDB, ISchueler_testsDB, ISchuelerDB, ISchulenDB, IStufenDB, ITesthefteDB, IUsersDB, IVergleichsgruppen_nwDB, IVergleichsgruppenwerteDB} from "./InterfacesDB";
import {IGruppe, IModifizierteTesthefteExport, ISchueler_testsAPI, IStatus, ITestheftAPI, IWasserstandSchule} from "./Interfaces";
import Veraportal from "./Veraportal";
import Modal from "./Modal";
import Utilities from "./Utilities";

/**
 * Stellt eine Schnittstelle zur VERA-API bereit und kapselt alle Serveranfragen.
 * Die Klasse ist als Singleton implementiert, um eine einzige Instanz für die gesamte Anwendung zu gewährleisten.
 */
export default class VeraApi {
    private _veraportal: Veraportal;

    /*
     * Singleton-Pattern
     * Stellt sicher, dass nur eine Instanz von VeraApi existiert.
     */
    private static _instance: VeraApi;

    /**
     * Gibt die Singleton-Instanz der VeraApi-Klasse zurück.
     * @param {Veraportal} veraportal - Die Hauptinstanz der Veraportal-Anwendung.
     * @returns {VeraApi} Die Singleton-Instanz.
     */
    public static getInstance(veraportal: Veraportal): VeraApi {
        return this._instance || (this._instance = new this(veraportal));
    }

    /**
     * Privater Konstruktor zur Implementierung des Singleton-Musters.
     * @param {Veraportal} veraportal - Die Hauptinstanz der Veraportal-Anwendung.
     * @private
     */
    private constructor(veraportal: Veraportal) {
        this._veraportal = veraportal;
    }

    /**
     * Behandelt API-Fehler, insbesondere Authentifizierungsfehler, indem auf die Logout-Seite umgeleitet wird.
     * @param {Response} response - Die HTTP-Antwort des Servers.
     * @returns {boolean} True, wenn ein Authentifizierungsfehler vorliegt und eine Umleitung initiiert wurde.
     */
    private _handleAuthError(response: Response): boolean {
        if (response.status === 401 || response.status === 403) {
            console.error(`Authentication error ${response.status}: ${response.statusText}. Redirecting to logout.`);
            window.location.href = '/logout.php';
            return true;
        }
        return false;
    }

    /*
     * Schule APIs
     * Methoden zur Interaktion mit Schuldaten.
     */

    /**
     * Ruft den aktuellen Status der Schule vom Server ab.
     * @returns {Promise<IStatus>} Ein Promise, das Statusinformationen der Schule zurückgibt.
     */
    public async fetchSchulstatus(): Promise<IStatus> {
        try {
            const response = await fetch(`/api/status.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /**
     * Setzt die Daten einer Schule zurück. Dies ist nur möglich, wenn noch keine Ergebnisdaten eingegeben wurden.
     * @param {string} schulnummer - Die Schulnummer der zurückzusetzenden Schule.
     * @returns {Promise<boolean>} Ein Promise, das `true` zurückgibt, wenn das Zurücksetzen erfolgreich war.
     */
    public async resetSchule(schulnummer: string): Promise<boolean> {
        try {
            const response = await fetch(`/api/reset-schule.php/`, {
                method: 'DELETE',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: schulnummer
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return false; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            // Klarnamenfunktion auch zurücksetzen
            localStorage.removeItem('tresor-passphrase');
            localStorage.removeItem('tresor-salt');
            localStorage.removeItem('tresor-encrypted');

            return await response.json();

        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return false;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Schule konnte nicht zurückgesetzt werden`,
                body: `<p>
                    Die Eingaben Ihrer Schule konnten nicht zurückgesetzt werden.<br/>
                    Wenn bereits Ergebnisdaten eingegeben wurden, kann die Schule nur noch über unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support zurückgesetzt werden.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });
        }
    }

    /*
     * User APIs
     * Methoden zur Verwaltung von Benutzerzugängen.
     */

    /**
     * Ruft alle Benutzerkonten ab, die einer Schule zugeordnet sind.
     * @returns {Promise<IUsersDB[]>} Ein Promise, das ein Array von Benutzerobjekten zurückgibt.
     */
    public async fetchUserArray(): Promise<(IUsersDB & IGruppenDB)[]> {
        try {
            const response = await fetch(`/api/koordinator/users.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /**
     * Erstellt einen neuen Benutzerzugang und eine Lerngruppe (Accounttyp 5) für die aktuell angemeldete Schule.
     * @returns {Promise<IUsersDB[]>} Ein Promise, das die aktualisierte Liste der Benutzerobjekte zurückgibt.
     */
    public async zugangUndLerngruppeErstellen(gruppenArr: (IGruppenDB & { schueler_anzahl: number; })[]): Promise<(IUsersDB & IGruppenDB)> {
        try {
            const response = await fetch(`/api/koordinator/users.php/`, {
                method: 'PUT',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    gruppen_array: gruppenArr
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Zugang konnte nicht erstellt werden`,
                body: `<p>
                    Beim Anlegen zusätzlicher Zugänge ist ein Fehler aufgetreten.<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 733260, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);
        }
    }

    /**
     * Fordert einen neuen Passwortlink an, wenn der alte verloren wurde.
     * @param {number} userId - Die ID des angeforderten Benutzers.
     * @returns {Promise<(IUsersDB & IGruppenDB)>>} Ein Promise, das die User und Gruppeninformationen für diesen User auflistet.
     */
    public async passwortlinkErneutAnfordern(userId: number): Promise<(IUsersDB & IGruppenDB)> {
        try {
            const response = await fetch(`/api/koordinator/users.php/`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    user_id: userId
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Zugang konnte nicht gelöscht werden`,
                body: `<p>
                    Beim Löschen des Zugangs ist ein Fehler aufgetreten.<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 739880, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);
        }
    }

    /**
     * Löscht einen bestehenden Benutzerzugang.
     * @param {number} userId - Die ID des zu löschenden Benutzers.
     * @returns {Promise<IUsersDB[]>} Ein Promise, das die aktualisierte Liste der Benutzerobjekte zurückgibt.
     */
    public async zugangLoeschen(userId: number): Promise<IUsersDB[]> {
        try {
            const response = await fetch(`/api/koordinator/users.php/`, {
                method: 'DELETE',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    user_id: userId
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Zugang konnte nicht gelöscht werden`,
                body: `<p>
                    Beim Löschen des Zugangs ist ein Fehler aufgetreten.<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 739880, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);
        }
    }

    /**
     * Speichert Änderungen am Testmodus der Schule.
     * @returns {Promise<boolean>} Ein Promise, das `true` zurückgibt, wenn die Speicherung erfolgreich war.
     */
    public async postTestmodus(modus: string): Promise<boolean> {
        try {
            const response = await fetch(`/api/testmodus-aendern.php`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    modus: modus
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return false; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return true
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return false;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Testmodus konnte nicht geändert werden`,
                body: `<p>
                    Der Testmodus kann nur geändert werden, <b>bevor</b> Ergebnisse in Ihrer Schule eingegeben wurden.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });
            return false;
        }
    }

    /**
     * Speichert die schulweite Testheftvariante für Lernstand 5.
     * @returns {Promise<boolean>} Ein Promise, das `true` zurückgibt, wenn die Speicherung erfolgreich war.
     */
    public async postTestheftvariante(schwierigkeit: number): Promise<boolean> {
        try {
            const response = await fetch(`/api/testheftvariante-waehlen.php`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    schwierigkeit: schwierigkeit
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return false;
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return true;
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return false;
            }
            const hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Testheftvariante konnte nicht gespeichert werden`,
                body: `<p>
                    Die Testheftvariante wurde bereits festgelegt oder konnte nicht gespeichert werden.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 300,
                cancelText: 'OK'
            });
            return false;
        }
    }

    /*
     * Schüler APIs
     * Methoden zur Verwaltung von Schülerdaten.
     */

    /**
     * Ruft alle Schülerdatensätze für die aktuell angemeldete Schule ab.
     * @returns {Promise<ISchuelerDB[]>} Ein Promise, das ein Array von Schülerobjekten zurückgibt.
     */
    public async fetchSchuelerArray(): Promise<ISchuelerDB[]> {
        try {
            const response = await fetch(`/api/schueler.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /**
     * Erstellt eine angegebene Anzahl leerer Schülerdatensätze.
     * @param {number} [anzahl=1] - Die Anzahl der zu erstellenden leeren Schülerdatensätze.
     * @returns {Promise<ISchuelerDB[]>} Ein Promise, das die neu erstellten Schülerobjekte zurückgibt.
     */
    public async postEmptySchuelers(anzahl: number = 1): Promise<ISchuelerDB[]> {
        try {
            let schuelerArray = [];
            for (let i = 0; i < anzahl; i++) {
                schuelerArray.push({});
            }

            const response = await fetch(`/api/schueler.php/`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    schueler_array: schuelerArray
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Schüler:in konnten nicht hinzugefügt werden`,
                body: `<p>
                    Beim Anlegen zusätzlicher Schüler ist ein Fehler aufgetreten.<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 98558, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);
        }
    }

    /**
     * Legt neue Schüler mit optionalen Anfangswerten an
     * @param {ISchuelerDB[]} schuelerArr - Ein Array neuer Schülerobjekte ohne Schülernummer
     * @returns {Promise<ISchuelerDB[]>} Ein Promise, das die neu angelegten Schülerobjekte zurückgibt
     */
    public async postSchueler(schuelerArr: ISchuelerDB[]): Promise<ISchuelerDB[]> {
        if (schuelerArr.length === 0)
            return [];

        try {
            const response = await fetch(`/api/schueler.php/`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    schueler_array: schuelerArr
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return []; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return [];
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Schüler:innen konnten nicht angelegt werden`,
                body: `<p>
                    Das Anlegen von Schüler:innen konnte nicht durchgeführt werden.<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 92511, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);

            return [];
        }
    }

    /**
     * Speichert die Eigenschaften (z.B. Geschlecht, Teilleistungsstörungen, etc.) für bestehende Schüler
     * @param {ISchuelerDB[]} schuelerArr - Ein Array von Schülerobjekten mit Schülernummer und Eigenschaften
     * @returns {Promise<ISchuelerDB[]>} Ein Promise, das die aktualisierten Schülerobjekte zurückgibt
     */
    public async putSchuelerEigenschaften(schuelerArr: ISchuelerDB[]): Promise<ISchuelerDB[]> {
        if (schuelerArr.length === 0)
            return [];

        try {
            const response = await fetch(`/api/schueler.php/`, {
                method: 'PUT',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    schueler_array: schuelerArr
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return []; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return [];
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Schüler-Eigenschaften konnten nicht abgespeichert werden`,
                body: `<p>
                    Das Abspeichern von Schüler-Eigenschaften konnte nicht durchgeführt werden.<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 92511, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);

            return [];
        }
    }

    /*
     * Gruppen APIs
     * Methoden zur Verwaltung von Gruppen (Stammklassen, Kurse).
     */

    /**
     * Ruft alle Gruppen für die aktuell angemeldete Schule ab.
     * @returns {Promise<IGruppe[]>} Ein Promise, das ein Array von Gruppenobjekten zurückgibt.
     */
    public async fetchGruppenArray(): Promise<IGruppe[]> {
        try {
            const response = await fetch(`/api/gruppen.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /**
     * Erstellt oder aktualisiert eine oder mehrere Gruppen.
     * @param {IGruppenDB[]} gruppenArr - Ein Array von Gruppenobjekten, die erstellt oder aktualisiert werden sollen.
     * @returns {Promise<IGruppenDB[]>} Ein Promise, das die erstellten oder aktualisierten Gruppenobjekte zurückgibt.
     */
    public async postGruppe(gruppenArr: IGruppenDB[]): Promise<IGruppenDB[]> {
        if (gruppenArr.length === 0)
            return [];

        try {
            const response = await fetch(`/api/gruppen.php/`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    gruppen_array: gruppenArr
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return []; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return [];
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Gruppe konnten nicht hinzugefügt/angepasst werden`,
                body: `<p>
                    Beim Anlegen/Anpassen der Gruppe ist ein Fehler aufgetreten.<br/>
                    Sollte das Problem wiederholt bestehen, loggen Sie sich neu ein und versuchen Sie es erneut.
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 185349, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);

            return [];
        }
    }

    /**
     * Löscht eine oder mehrere Gruppen. Eine Gruppe kann nur gelöscht werden, wenn ihr keine Schüler mehr zugewiesen sind.
     * @param {IGruppenDB[]} gruppenArr - Ein Array von Gruppenobjekten, die gelöscht werden sollen.
     * @returns {Promise<boolean>} Ein Promise, das `true` zurückgibt, wenn das Löschen erfolgreich war.
     */
    public async deleteGruppe(gruppenArr: IGruppenDB[]): Promise<boolean> {
        if (gruppenArr.length === 0)
            return false;

        try {
            const response = await fetch(`/api/gruppen.php/`, {
                method: 'DELETE',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    gruppen_array: gruppenArr
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return false; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();

        } catch (e) {
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return false;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Nur leere Gruppen können gelöscht werden`,
                body: `<p>
                    Es können nur Gruppen gelöscht werden, denen keine Schüler mehr zugewiesen sind.<br/>
                    Entfernen Sie die Zuordnungen in "Stammklassen und Kurse zuordnen" und löschen Sie anschließend die Gruppe.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            // Utilities.errorToSentry(e);
        }
    }

    /*
     * Zuweisungen der SuS zu Gruppen APIs
     * Methoden zur Verwaltung der Zuordnung von Schülern zu Gruppen.
     */

    /**
     * Ruft alle Zuordnungen von Schülern zu Gruppen für die aktuelle Schule ab.
     * @returns {Promise<ISchueler_gruppenDB[]>} Ein Promise, das ein Array von Schüler-Gruppen-Zuweisungsobjekten zurückgibt.
     */
    public async fetchSchuelerGruppenZuweisungenArray(): Promise<ISchueler_gruppenDB[]> {
        try {
            const response = await fetch(`/api/schueler_gruppen.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /**
     * Speichert die Zuordnungen von Schülern zu Gruppen.
     * @param {ISchueler_gruppenDB[]} schuelerGruppenArr - Ein Array von Zuweisungsobjekten.
     * @param {boolean} [getFullResponse=false] - Gibt an, ob die vollständige Liste aller Zuweisungen zurückgegeben werden soll.
     * @returns {Promise<ISchueler_gruppenDB[]>} Ein Promise, das die gespeicherten Zuweisungsobjekte zurückgibt.
     */
    public async postSchuelerGruppenZuweisungen(schuelerGruppenArr: ISchueler_gruppenDB[], getFullResponse: boolean = false): Promise<ISchueler_gruppenDB[]> {
        if (schuelerGruppenArr.length === 0)
            return [];

        try {
            const response = await fetch(`/api/schueler_gruppen.php/`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    schueler_gruppen_array: schuelerGruppenArr,
                    get_full_response: (getFullResponse) ? 1 : 0
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return []; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return [];
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Gruppenzuweisungen konnten nicht abgespeichert werden`,
                body: `<p>
                    Das Abspeichern von Gruppenzuweisungen konnte nicht durchgeführt werden.<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 38367, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);

            return [];
        }
    }

    /**
     * Löscht die Zuordnungen von Schülern zu Gruppen.
     * @param {ISchueler_gruppenDB[]} schuelerGruppenArr - Ein Array von Zuweisungsobjekten, die gelöscht werden sollen.
     * @returns {Promise<ISchueler_gruppenDB[]>} Ein Promise, das ein leeres Array oder die verbleibenden Zuweisungen zurückgibt.
     */
    public async deleteSchuelerGruppenZuweisungen(schuelerGruppenArr: ISchueler_gruppenDB[]): Promise<ISchueler_gruppenDB[]> {
        if (schuelerGruppenArr.length === 0)
            return [];

        try {
            const response = await fetch(`/api/schueler_gruppen.php/`, {
                method: 'DELETE',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    schueler_gruppen_array: schuelerGruppenArr
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();

        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Gruppenzuweisungen konnten nicht zurückgesetzt werden`,
                body: `<p>
                    Das Zurücksetzen von Gruppenzuweisungen konnte nicht durchgeführt werden.<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 865332, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);
        }
    }

    /*
     * Testhefte APIs
     * Methoden zum Abrufen von Testheftinformationen.
     */

    /**
     * Ruft alle verfügbaren Testhefte für die aktuelle Schule ab.
     * @returns {Promise<ITestheftAPI[]>} Ein Promise, das ein Array von Testheftobjekten zurückgibt.
     */
    public async fetchTesthefteArray(): Promise<ITestheftAPI[]> {
        try {
            const response = await fetch(`/api/testhefte.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /** Ruft die didaktischen Zusatzinformationen zu Items ab */
    public async fetchDidaktischeKommentareItems(): Promise<IDidaktische_kommentare_itemsDB[]> {
        try {
            const response = await fetch(`/api/didaktische-kommentare-items.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response))
                    return [];
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);
            return [];
        }
    }

    /** Ruft die didaktischen Zusatzinformationen zu Aufgaben ab */
    public async fetchDidaktischeKommentareAufgaben(): Promise<IDidaktische_kommentare_aufgabenDB[]> {
        try {
            const response = await fetch(`/api/didaktische-kommentare-aufgaben.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response))
                    return [];
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);
            return [];
        }
    }

    /*
     * Zuweisungen der SuS zu Tests APIs
     * Methoden zur Verwaltung der Zuordnung von Schülern zu Tests.
     */

    /**
     * Ruft die Testzuweisungen für alle Schüler der aktuellen Schule ab.
     * @returns {Promise<ISchueler_testsAPI[]>} Ein Promise, das ein Array von Schüler-Test-Zuweisungsobjekten zurückgibt.
     */
    public async fetchTestsArray(): Promise<ISchueler_testsAPI[]> {
        try {
            const response = await fetch(`/api/schueler_tests.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /*
     * Ergebnisse der SuS APIs
     * Methoden zur Verwaltung von Schülerergebnissen.
     */

    /**
     * Überträgt die eingegebenen Testergebnisse an den Server.
     * @param {IErgebnisseDB[]} ergebnisseArr - Ein Array von Ergebnisobjekten, die gespeichert werden sollen.
     * @returns {Promise<boolean>} Ein Promise, das `true` zurückgibt, wenn die Speicherung erfolgreich war.
     */
    public async postErgebnisse(ergebnisseArr: IErgebnisseDB[]): Promise<boolean> {
        if (ergebnisseArr.length === 0)
            return false;

        try {
            const response = await fetch(`/api/ergebnisse.php/`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    ergebnisse_array: ergebnisseArr
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return false; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return true
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return false;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Ergebnisse konnten nicht abgespeichert werden`,
                body: `<p>
                    Ihre Eintragungen der letzten 30 Sekunden konnten nicht gespeichert werden. Ist Ihre Internetkonnektivität sichergestellt?<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 766423, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);

            return false;
        }
    }

    /**
     * Speichert die An- oder Abwesenheit von Schülern für einen bestimmten Test.
     * @param {({schueler_id: number, nicht_anwesend: number, schueler_test_id: number})[]} abwesenheitArr - Ein Array von Objekten, die die Abwesenheitsinformationen enthalten.
     * @returns {Promise<boolean>} Ein Promise, das `true` zurückgibt, wenn die Speicherung erfolgreich war.
     */
    public async postAbwesenheiten(abwesenheitArr: ({ schueler_id: number; nicht_anwesend: number; schueler_test_id: number; })[]): Promise<boolean> {
        try {
            const response = await fetch(`/api/schueler_toggle-anwesend.php/`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    schueler_tests_array: abwesenheitArr
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return false; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return true
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return false;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Abwesenheiten konnten nicht abgespeichert werden`,
                body: `<p>
                    Ihre Abwesenheitseintragungen der letzten 30 Sekunden konnten nicht gespeichert werden. Ist Ihre Internetkonnektivität sichergestellt?<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 766499, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);

            return false;
        }
    }

    /*
     * Kompetenzstufengrenzen APIs
     * Methoden zum Abrufen von Kompetenzstufengrenzen.
     */

    /**
     * Ruft die Kompetenzstufengrenzen vom Server ab.
     * @returns {Promise<IStufenDB[]>} Ein Promise, das ein Array von Stufengrenzen-Objekten zurückgibt.
     */
    public async fetchStufengrenzen(): Promise<IStufenDB[]> {
        try {
            const response = await fetch(`/api/stufen.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /*
     * Bistas APIs
     * Methoden zum Abrufen von Bildungsstandards (Bistas).
     */

    /**
     * Ruft die Bistapunkte (Bistas) vom Server ab.
     * @returns {Promise<IBistasDB[]>} Ein Promise, das ein Array von Bista-Objekten zurückgibt.
     */
    public async fetchBistas(): Promise<IBistasDB[]> {
        try {
            const response = await fetch(`/api/bistas.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /*
     * Lösungshäufigkeiten APIs
     * Methoden zum Abrufen von Lösungshäufigkeiten auf Item-Ebene für verschiedene Vergleichsgruppen.
     */

    /**
     * Ruft die Lösungshäufigkeiten auf Item-Ebene für verschiedene Vergleichsgruppen ab.
     * @returns {Promise<ILoesungshaeufigkeitenDB[]>} Ein Promise, das ein Array von Lösungshäufigkeit-Objekten zurückgibt.
     */
    public async fetchLhItemebeneVergleichsgruppen(): Promise<ILoesungshaeufigkeitenDB[]> {
        try {
            const response = await fetch(`/api/lh-itemebene-vergleichsgruppen.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    public async fetchVergleichsgruppenwerte(): Promise<IVergleichsgruppenwerteDB[]> {
        try {
            const response = await fetch(`/api/vergleichsgruppenwerte.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    public async fetchVergleichsgruppenNw(): Promise<IVergleichsgruppen_nwDB[]> {
        try {
            const response = await fetch(`/api/vergleichsgruppen_nw.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /*
     * Tresor-API
     * Methoden zur Verwaltung der clientseitig verschlüsselten Schülernamen (Klarnamen).
     */

    /**
     * Setzt die Klarnamenfunktion für eine Schule zurück, indem die verschlüsselten Daten gelöscht werden.
     * @param {IJsontresorDB[]} schulIdArr - Ein Array, das die zu löschenden Tresor-Einträge identifiziert.
     * @returns {Promise<boolean>} Ein Promise, das `true` zurückgibt, wenn das Zurücksetzen erfolgreich war.
     */
    public async deleteTresor(schulIdArr: IJsontresorDB[]): Promise<boolean> {
        if (schulIdArr.length === 0)
            return false;

        try {
            const response = await fetch(`/api/tresor.php/`, {
                method: 'DELETE',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    schulen_array: schulIdArr
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return false; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();

        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return false;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Klarnamenfunktion konnte nicht zurückgesetzt werden`,
                body: `<p>
                    Beim Zurücksetzen der Klarnamen Ihrer Schule ist ein Fehler aufgetreten.<br/>
                    Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 264378, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });
        }
    }

    /*
     * Domänen API
     * Methoden zum Abrufen von Domäneninformationen.
     */

    /**
     * Ruft die Fachdomänen für die aktuelle Schule ab.
     * @returns {Promise<IDomaenenDB[]>} Ein Promise, das ein Array von Domänen-Objekten zurückgibt.
     */
    public async fetchDomaenenArray(): Promise<IDomaenenDB[]> {
        try {
            const response = await fetch(`/api/domaenen.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /*
     * Wasserstand APIs
     * Methoden zur Überwachung und Verwaltung des Bearbeitungsstatus von Schulen (nur für Ländervertreter).
     */

    /**
     * Ruft den Bearbeitungsstatus aller Schulen für die Wasserstandsanzeige ab.
     * @returns {Promise<IWasserstandSchule[]>} Ein Promise, das ein Array von Objekten mit kombinierten Status- und Schulinformationen zurückgibt.
     */
    public async fetchWasserstandSchulen(): Promise<IWasserstandSchule[]> {
        try {
            const response = await fetch(`/api/wasserstand/schulen.php`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(120000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /**
     * Ruft für den Export im Wasserstand alle bestellten mod. Testmaterialien (NW) ab
     * @returns {Promise<IModifizierteTesthefteExport[]>} Ein Promise auf die modifizierten Testmaterialien pro Schulnummer.
     */
    public async fetchModifizierteTesthefte(): Promise<IModifizierteTesthefteExport[]> {
        try {
            const response = await fetch(`/api/wasserstand/modifizierte-testhefte.php`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    /**
     * Speichert Änderungen am Status einer oder mehrerer Schulen.
     * @param {{schulnummer: string, statustyp: string}[]} changes - Ein Array von Objekten, die die Änderungen am Schulstatus definieren.
     * @returns {Promise<boolean>} Ein Promise, das `true` zurückgibt, wenn die Speicherung erfolgreich war.
     */
    public async postSchulstatus(changes: { schulnummer: string; statustyp: number; }[]): Promise<boolean> {
        if (changes.length === 0)
            return false;

        try {
            const response = await fetch(`/api/wasserstand/schulstatus.php`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    changes: changes
                })
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return false; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return true
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return false;
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Schulstatus konnte nicht abgespeichert werden`,
                body: `<p>
                    Ihre Eintragungen der letzten 30 Sekunden konnten nicht gespeichert werden. Ist Ihre Internetkonnektivität sichergestellt?
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });
            return false;
        }
    }

    /**
     * Fordert ein Einmal-Authentifizierungstoken für einen bestimmten Benutzer an.
     * @param {string} username - Der Benutzername (Schulnummer), für den das Token angefordert wird.
     * @returns {Promise<string>} Ein Promise, das das Authentifizierungstoken als String zurückgibt.
     */
    public async getSingleAuthToken(username: string): Promise<string> {
        try {
            const response = await fetch(`/api/wasserstand/get-single-auth-token.php/username/${encodeURIComponent(username)}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

    public async generatePasswortanforderungLink(username: string): Promise<{ passwort_anforderung_hash: string }> {
        try {
            const response = await fetch(`/api/wasserstand/passwortlink-generieren.php`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                body: JSON.stringify({
                    username: username
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                if (this._handleAuthError(response)) return; // Authentifizierungsfehler behandeln
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            console.error(e);
            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            // Wenn der Fehler bereits durch _handleAuthError behandelt wurde, nicht erneut werfen.
            if (e instanceof Error && (e.message.includes('401') || e.message.includes('403'))) {
                return;
            }
        }
    }

}
